import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useMeta } from "../lib/meta";
import type { Card, MilestoneProgress } from "../lib/types";
import { formatINR } from "../lib/format";
import { Badge, BlockedAction, EmptyState, ErrorBanner, Modal, PageHeader, Panel, ProgressBar, Spinner } from "../components/ui";

/** Milestones measure either rupees or a count of qualifying transactions. */
function progressLabel(m: MilestoneProgress, value: number): string {
  if (m.metric !== "TRANSACTION_COUNT") return formatINR(value);
  const n = Math.round(value);
  return `${n} transaction${n === 1 ? "" : "s"}`;
}

export default function MilestonesPage() {
  const meta = useMeta();
  const [milestones, setMilestones] = useState<MilestoneProgress[] | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<{ open: boolean; milestone: MilestoneProgress | null }>({
    open: false,
    milestone: null,
  });

  const load = useCallback(async () => {
    try {
      const [m, c] = await Promise.all([
        api.get<{ milestones: MilestoneProgress[] }>("/milestones"),
        api.get<{ cards: Card[] }>("/cards"),
      ]);
      setMilestones(m.milestones);
      setCards(c.cards);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load milestones");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function remove(m: MilestoneProgress) {
    if (!confirm(`Delete the milestone "${m.name}"?`)) return;
    try {
      await api.del(`/milestones/${m.id}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete the milestone");
    }
  }

  if (!meta || !milestones) return <Spinner />;

  const inProgress = milestones.filter((m) => !m.achieved);
  const achieved = milestones.filter((m) => m.achieved);

  return (
    <>
      <PageHeader
        title="Milestones & fee waivers"
        subtitle="Spend thresholds that unlock vouchers, bonus points or a waived annual fee."
        actions={
          <BlockedAction blocked={cards.length === 0} reason="Add a card first — a milestone belongs to one">
            <button className="btn-primary" onClick={() => setModal({ open: true, milestone: null })} disabled={cards.length === 0}>
              Add milestone
            </button>
          </BlockedAction>
        }
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {milestones.length === 0 ? (
        <EmptyState
          title="No milestones yet"
          hint="Catalogue cards come with their milestones and fee waivers pre-filled. Add a card, or create a milestone by hand."
          action={<Link to="/cards" className="btn-primary">Add a card</Link>}
        />
      ) : (
        <div className="space-y-6">
          <Panel title="In progress" subtitle={`${inProgress.length} to go`}>
            {inProgress.length === 0 ? (
              <EmptyState title="Everything's been hit for the current period" />
            ) : (
              <ul className="space-y-5">
                {inProgress.map((m) => (
                  <MilestoneRow
                    key={m.id}
                    milestone={m}
                    onEdit={() => setModal({ open: true, milestone: m })}
                    onDelete={() => remove(m)}
                  />
                ))}
              </ul>
            )}
          </Panel>

          {achieved.length > 0 && (
            <Panel title="Achieved this period" subtitle={`${achieved.length} unlocked`}>
              <ul className="space-y-5">
                {achieved.map((m) => (
                  <MilestoneRow
                    key={m.id}
                    milestone={m}
                    onEdit={() => setModal({ open: true, milestone: m })}
                    onDelete={() => remove(m)}
                  />
                ))}
              </ul>
            </Panel>
          )}
        </div>
      )}

      <MilestoneModal
        state={modal}
        cards={cards}
        types={meta.milestoneTypes}
        periods={meta.milestonePeriods}
        onClose={() => setModal({ open: false, milestone: null })}
        onSaved={() => { setModal({ open: false, milestone: null }); void load(); }}
      />
    </>
  );
}

function MilestoneRow({
  milestone: m,
  onEdit,
  onDelete,
}: {
  milestone: MilestoneProgress;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <li>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-medium text-ink-900">
            {m.name}
            {m.type === "FEE_WAIVER" && <Badge tone="info">Fee waiver</Badge>}
            {m.achieved && <Badge tone="good">Achieved</Badge>}
          </p>
          <p className="text-xs text-ink-500">
            {m.cardName} · {m.rewardDescription}
            {m.rewardValue > 0 && ` · worth ${formatINR(m.rewardValue)}`}
            {m.metric === "TRANSACTION_COUNT" && m.minTransactionAmount
              ? ` · counts spends of ${formatINR(m.minTransactionAmount)}+`
              : ""}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="tnum text-xs text-ink-500">
            {progressLabel(m, m.spent)} / {progressLabel(m, m.threshold)}
          </span>
          <button className="text-xs text-accent-700 underline" onClick={onEdit}>Edit</button>
          <button className="text-xs text-red-700 underline" onClick={onDelete}>Delete</button>
        </div>
      </div>
      <div className="mt-2">
        <ProgressBar pct={m.pct} achieved={m.achieved} />
      </div>
      <p className="mt-1.5 text-xs text-ink-500">
        {m.achieved ? (
          <>Unlocked — resets in {m.daysLeft} day{m.daysLeft === 1 ? "" : "s"}</>
        ) : (
          <>
            <strong className="tnum text-ink-700">{progressLabel(m, m.remaining)}</strong> to go · {m.daysLeft} day
            {m.daysLeft === 1 ? "" : "s"} left · {m.windowLabel}
          </>
        )}
      </p>
    </li>
  );
}

function MilestoneModal({
  state,
  cards,
  types,
  periods,
  onClose,
  onSaved,
}: {
  state: { open: boolean; milestone: MilestoneProgress | null };
  cards: Card[];
  types: string[];
  periods: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    cardId: "", name: "", type: "SPEND_MILESTONE", metric: "SPEND", threshold: "",
    minTransactionAmount: "", period: "ANNIVERSARY_YEAR", rewardDescription: "",
    rewardValue: "0", rewardPoints: "0", isActive: true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const m = state.milestone;
    setForm({
      cardId: m?.cardId ?? cards[0]?.id ?? "",
      name: m?.name ?? "",
      type: m?.type ?? "SPEND_MILESTONE",
      metric: m?.metric ?? "SPEND",
      threshold: m ? String(m.threshold) : "",
      minTransactionAmount: m?.minTransactionAmount != null ? String(m.minTransactionAmount) : "",
      period: m?.period ?? "ANNIVERSARY_YEAR",
      rewardDescription: m?.rewardDescription ?? "",
      rewardValue: String(m?.rewardValue ?? 0),
      rewardPoints: String(m?.rewardPoints ?? 0),
      isActive: m?.isActive ?? true,
    });
  }, [state.milestone, state.open, cards]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const payload = {
      cardId: form.cardId,
      name: form.name.trim(),
      type: form.type,
      metric: form.metric,
      threshold: Number(form.threshold),
      minTransactionAmount:
        form.metric === "TRANSACTION_COUNT" && form.minTransactionAmount
          ? Number(form.minTransactionAmount)
          : null,
      period: form.period,
      rewardDescription: form.rewardDescription.trim(),
      rewardValue: Number(form.rewardValue) || 0,
      rewardPoints: Number(form.rewardPoints) || 0,
      isActive: form.isActive,
    };
    try {
      if (state.milestone) await api.put(`/milestones/${state.milestone.id}`, payload);
      else await api.post("/milestones", payload);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the milestone");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={state.open} onClose={onClose} title={state.milestone ? "Edit milestone" : "Add milestone"}>
      <form onSubmit={save} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="m-card">Card</label>
            <select id="m-card" className="input" required value={form.cardId} onChange={(e) => setForm({ ...form, cardId: e.target.value })}>
              {cards.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="m-type">Type</label>
            <select id="m-type" className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {types.map((t) => (
                <option key={t} value={t}>{t === "FEE_WAIVER" ? "Annual fee waiver" : "Spend milestone"}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="m-name">Name</label>
            <input id="m-name" className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. ₹4L annual milestone" />
          </div>
          <div>
            <label className="label" htmlFor="m-metric">Measured by</label>
            <select id="m-metric" className="input" value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })}>
              <option value="SPEND">Amount spent</option>
              <option value="TRANSACTION_COUNT">Number of transactions</option>
            </select>
          </div>
          <div>
            <label className="label" htmlFor="m-threshold">
              {form.metric === "TRANSACTION_COUNT" ? "Transactions needed" : "Spend threshold (₹)"}
            </label>
            <input
              id="m-threshold"
              className="input tnum"
              type="number"
              min={1}
              step={form.metric === "TRANSACTION_COUNT" ? 1 : "any"}
              required
              value={form.threshold}
              onChange={(e) => setForm({ ...form, threshold: e.target.value })}
            />
          </div>
          {form.metric === "TRANSACTION_COUNT" && (
            <div>
              <label className="label" htmlFor="m-min">Each transaction must be at least (₹)</label>
              <input
                id="m-min"
                className="input tnum"
                type="number"
                min={0}
                value={form.minTransactionAmount}
                onChange={(e) => setForm({ ...form, minTransactionAmount: e.target.value })}
                placeholder="Any amount"
              />
              <p className="mt-1 text-xs text-ink-500">Smaller spends won't count toward this one.</p>
            </div>
          )}
          <div>
            <label className="label" htmlFor="m-period">Measured over</label>
            <select id="m-period" className="input" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })}>
              {periods.map((p) => (
                <option key={p} value={p}>
                  {p === "ANNIVERSARY_YEAR" ? "Card anniversary year"
                    : p === "CALENDAR_YEAR" ? "Calendar year"
                    : p === "QUARTER" ? "Calendar quarter" : "Calendar month"}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="m-value">Reward worth (₹)</label>
            <input id="m-value" className="input tnum" type="number" min={0} value={form.rewardValue} onChange={(e) => setForm({ ...form, rewardValue: e.target.value })} />
          </div>
        </div>

        <div>
          <label className="label" htmlFor="m-desc">What you get</label>
          <input id="m-desc" className="input" required value={form.rewardDescription} onChange={(e) => setForm({ ...form, rewardDescription: e.target.value })} placeholder="e.g. ₹5,000 gift voucher" />
        </div>

        <div>
          <label className="label" htmlFor="m-points">Bonus points awarded (optional)</label>
          <input id="m-points" className="input tnum" type="number" min={0} value={form.rewardPoints} onChange={(e) => setForm({ ...form, rewardPoints: e.target.value })} />
        </div>

        <label className="flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          />
          Active — track this and factor it into the optimiser
        </label>

        <p className="text-xs text-ink-500">
          Anniversary-year milestones need the card's anniversary date set, otherwise they fall back to the calendar year.
        </p>

        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Save milestone"}</button>
        </div>
      </form>
    </Modal>
  );
}
